'use client';

import React from 'react';

import { Box, BoxProps } from './box';

export interface CodeProps extends BoxProps {
  children: React.ReactNode;
  variant?: 'subtle' | 'solid' | 'outline';
  colorPalette?: 'gray' | 'blue' | 'green' | 'red' | 'yellow' | 'purple';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Code component for displaying inline code snippets
 * Provides consistent styling for code elements
 */
// Filter out Chakra UI specific props that shouldn't be passed to DOM elements
const filterBoxProps = (props: Record<string, unknown>): Record<string, unknown> => {
  const {
    // Chakra UI specific props that shouldn't go to DOM
    borderWidth: _borderWidth,
    borderStyle: _borderStyle,
    borderColor: _borderColor,
    borderRadius: _borderRadius,
    backgroundColor: _backgroundColor,
    bg: _bg,
    color: _color,
    opacity: _opacity,
    visibility: _visibility,
    display: _display,
    position: _position,
    top: _top,
    right: _right,
    bottom: _bottom,
    left: _left,
    zIndex: _zIndex,
    shadow: _shadow,
    boxShadow: _boxShadow,
    transition: _transition,
    transform: _transform,
    width: _width,
    height: _height,
    minWidth: _minWidth,
    maxWidth: _maxWidth,
    minHeight: _minHeight,
    maxHeight: _maxHeight,
    padding: _padding,
    paddingTop: _paddingTop,
    paddingRight: _paddingRight,
    paddingBottom: _paddingBottom,
    paddingLeft: _paddingLeft,
    paddingX: _paddingX,
    paddingY: _paddingY,
    margin: _margin,
    marginTop: _marginTop,
    marginRight: _marginRight,
    marginBottom: _marginBottom,
    marginLeft: _marginLeft,
    marginX: _marginX,
    marginY: _marginY,
    overflow: _overflow,
    textAlign: _textAlign,
    verticalAlign: _verticalAlign,
    whiteSpace: _whiteSpace,
    wordBreak: _wordBreak,
    fontSize: _fontSize,
    fontWeight: _fontWeight,
    lineHeight: _lineHeight,
    letterSpacing: _letterSpacing,
    fontFamily: _fontFamily,
    cursor: _cursor,
    userSelect: _userSelect,
    pointerEvents: _pointerEvents,
    ...filteredProps
  } = props;

  return filteredProps;
};

export const Code = React.forwardRef<HTMLElement, CodeProps>(
  ({ children, variant = 'subtle', colorPalette = 'gray', size = 'md', ...props }, ref) => {
    const sizeConfig = {
      sm: {
        fontSize: 'xs',
        paddingX: 1,
        paddingY: 0.5,
      },
      md: {
        fontSize: 'sm',
        paddingX: 2,
        paddingY: 1,
      },
      lg: {
        fontSize: 'md',
        paddingX: 3,
        paddingY: 1.5,
      },
    };

    const colorSchemes = {
      gray: {
        subtle: {
          backgroundColor: '#F7FAFC',
          color: '#2D3748',
          border: '1px solid #E2E8F0',
        },
        solid: {
          backgroundColor: '#4A5568',
          color: 'white',
          border: '1px solid #4A5568',
        },
        outline: {
          backgroundColor: 'transparent',
          color: '#4A5568',
          border: '1px solid #4A5568',
        },
      },
      blue: {
        subtle: {
          backgroundColor: '#EBF8FF',
          color: '#2C5282',
          border: '1px solid #90CDF4',
        },
        solid: {
          backgroundColor: '#3182CE',
          color: 'white',
          border: '1px solid #3182CE',
        },
        outline: {
          backgroundColor: 'transparent',
          color: '#3182CE',
          border: '1px solid #3182CE',
        },
      },
      green: {
        subtle: {
          backgroundColor: '#F0FFF4',
          color: '#22543D',
          border: '1px solid #9AE6B4',
        },
        solid: {
          backgroundColor: '#38A169',
          color: 'white',
          border: '1px solid #38A169',
        },
        outline: {
          backgroundColor: 'transparent',
          color: '#38A169',
          border: '1px solid #38A169',
        },
      },
      red: {
        subtle: {
          backgroundColor: '#FED7D7',
          color: '#742A2A',
          border: '1px solid #FEB2B2',
        },
        solid: {
          backgroundColor: '#E53E3E',
          color: 'white',
          border: '1px solid #E53E3E',
        },
        outline: {
          backgroundColor: 'transparent',
          color: '#E53E3E',
          border: '1px solid #E53E3E',
        },
      },
      yellow: {
        subtle: {
          backgroundColor: '#FFFBEB',
          color: '#744210',
          border: '1px solid #F6E05E',
        },
        solid: {
          backgroundColor: '#D69E2E',
          color: 'white',
          border: '1px solid #D69E2E',
        },
        outline: {
          backgroundColor: 'transparent',
          color: '#D69E2E',
          border: '1px solid #D69E2E',
        },
      },
      purple: {
        subtle: {
          backgroundColor: '#FAF5FF',
          color: '#44337A',
          border: '1px solid #D6BCFA',
        },
        solid: {
          backgroundColor: '#805AD5',
          color: 'white',
          border: '1px solid #805AD5',
        },
        outline: {
          backgroundColor: 'transparent',
          color: '#805AD5',
          border: '1px solid #805AD5',
        },
      },
    };

    const config = sizeConfig[size as keyof typeof sizeConfig];
    const colorConfig =
      colorSchemes[colorPalette as keyof typeof colorSchemes][
        variant as keyof typeof colorSchemes.gray
      ];

    return (
      <Box
        ref={ref as React.Ref<HTMLDivElement>}
        as="code"
        display="inline"
        fontFamily="'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace"
        fontWeight="semibold"
        borderRadius="md"
        whiteSpace="nowrap"
        verticalAlign="baseline"
        fontSize={config.fontSize}
        paddingX={config.paddingX}
        paddingY={config.paddingY}
        {...colorConfig}
        {...filterBoxProps(props)}
      >
        {children}
      </Box>
    );
  }
);

Code.displayName = 'Code';

export default Code;
